import { useAuth, useOrganizationList } from '@clerk/clerk-react';
import { isAxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { joinOrg } from '@/actions/auth/joinOrg';
import { toast } from '@/hooks/ui/use-toast';

interface JoinOrgParams {
  role: 'org:zee';
  orgId: string;
  userId: string;
}

export const JoinOrg = () => {
  const hasCalled = useRef(false);

  const navigate = useNavigate();
  const { isLoaded, userId, isSignedIn } = useAuth();
  const {
    isLoaded: isLoadedOrg,
    userMemberships,
    setActive: setActiveOrg,
  } = useOrganizationList({
    userMemberships: { infinite: true, keepPreviousData: true },
  });

  if (!isLoaded || !isLoadedOrg || userMemberships.isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }

  const handleJoinOrg = async (): Promise<void> => {
    if (!userId || !isSignedIn) {
      return;
    }

    const activeOrgId = window.localStorage.getItem('active-org-id');
    if (!activeOrgId) {
      console.error('No active organization ID found');
      return;
    }

    const params: JoinOrgParams = {
      role: 'org:zee',
      orgId: activeOrgId,
      userId,
    };

    try {
      if (hasCalled.current) {
        return;
      }

      hasCalled.current = true;
      const isMemberOfActiveOrg = userMemberships.data?.some(
        (membership) => membership.organization.id === activeOrgId
      );

      if (!isMemberOfActiveOrg) {
        await joinOrg(params);
      }

      await setActiveOrg({
        redirectUrl: '/dashboard',
        organization: activeOrgId,
      });
    } catch (error) {
      hasCalled.current = false;
      const status = isAxiosError(error) ? error.response?.status : undefined;
      const description =
        status === 403
          ? 'You need an active invitation to join this organization. Please contact your organization admin.'
          : 'Failed to join organization. Please try again later.';

      console.error('Failed to join organization:', error);
      toast({
        title: 'Access Denied',
        description,
        variant: 'destructive',
      });
      navigate(`/${activeOrgId}/auth/sign-in`);
    }
  };

  handleJoinOrg();

  return (
    <div className="flex h-screen w-full items-center justify-center">
      <div className="text-center">
        <div className="mb-4 flex items-center justify-center">
          <Loader2 className="size-4 animate-spin" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900">
          Joining Organization
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          Please wait while we add you to the organization...
        </p>
      </div>
    </div>
  );
};
